import React, { useEffect, useState } from 'react';

import { climbBridgeAPI, toolsAPI } from '../../../lib/api';

const TOOL_KEY = 'climb-bridge';
const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const LABEL = 'block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide';

const REDACTED = '[configured]';

const STREAMS = [
  { key: 'standings', label: 'onair.standings', hint: 'The leaderboard for the on-air lane. The one graphic you cannot go on air without.' },
  { key: 'climber', label: 'onair.climber', hint: 'The selected athlete in full detail — lower thirds.' },
  { key: 'context', label: 'onair.context', hint: 'Event, round and lane titles, plus the start list for the Control App dropdown.' },
  { key: 'events', label: 'onair.events', hint: 'Derived moments — a top, a rank change — with a TTL so a late renderer replays nothing.' },
  { key: 'health', label: 'bridge.health', hint: 'Diagnostics, for a debug overlay. Not for air.' },
];

const emptyStreams = () => Object.fromEntries(STREAMS.map(s => [s.key, '']));

/**
 * Screen 1 — Setup.
 *
 * Enter a competition ID, confirm the lane mapping, bind the streams, start.
 * The lane table is the important part of this screen: it is where a lane that
 * maps to anything other than exactly one circuit-challenge node has to be
 * caught, because the alternative is discovering it on air.
 */
export default function SetupPanel({ show, running, onStarted }) {
  const [competitionInput, setCompetitionInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState('');

  const [laneId, setLaneId] = useState(null);
  // Which lanes the bridge follows: polls, records and publishes. null until a
  // competition resolves, then every lane that binds.
  const [followIds, setFollowIds] = useState(null);
  const [streams, setStreams] = useState(emptyStreams);
  const [tuning, setTuning] = useState({ poll_interval_ms: 1000, off_air_poll_ms: 10000, visible_rows: 12, auto_follow: false, record_on_start: true });

  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState('');
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => { loadPresets(); }, []);

  function loadPresets() {
    toolsAPI.presets(TOOL_KEY)
      .then(res => setPresets(res.data || []))
      .catch(() => setPresets([]));
  }

  async function handleResolve(idOverride) {
    const value = idOverride ?? competitionInput;
    if (!String(value).trim()) return;
    setResolving(true);
    setError('');
    try {
      const res = await climbBridgeAPI.resolve({ competition_id: String(value).trim() });
      setResolved(res.data);
      const bindable = (res.data.lanes || []).filter(l => l.bindable);
      setLaneId(prev => prev ?? bindable[0]?.laneId ?? null);
      setFollowIds(prev => prev ?? bindable.map(l => l.laneId));
      return res.data;
    } catch (err) {
      setResolved(null);
      setError(err.response?.data?.error || err.message || 'Could not reach ClimbAlong');
    } finally {
      setResolving(false);
    }
  }

  async function applyPreset(id) {
    setPresetId(id);
    if (!id) return;
    const preset = presets.find(p => p.id === id);
    if (!preset) return;

    const cfg = preset.config || {};
    setPresetName(preset.name);
    setCompetitionInput(String(cfg.competition_id ?? ''));
    setTuning(t => ({ ...t, ...(cfg.tuning || {}) }));
    // Tokens come back redacted. Showing the placeholder is honest — the real
    // value never left the server, and /start resolves it from the preset row.
    setStreams(Object.fromEntries(STREAMS.map(s => [s.key, cfg.streams?.[s.key] ? REDACTED : ''])));

    setFollowIds(cfg.lane_ids?.length ? cfg.lane_ids : null);
    const data = await handleResolve(cfg.competition_id);
    if (data && cfg.lane_id != null) setLaneId(cfg.lane_id);
  }

  async function savePreset() {
    if (!presetName.trim() || !resolved) return;
    setSavingPreset(true);
    setError('');

    const config = {
      competition_id: resolved.competition_id,
      lane_id: laneId,
      lane_ids: followed,
      tuning,
      // A field left at the placeholder is not sent, so the stored token stands.
      streams: Object.fromEntries(STREAMS.map(s => [s.key, streams[s.key] || undefined])),
    };

    try {
      const existing = presets.find(p => p.name === presetName.trim());
      const res = existing
        ? await toolsAPI.updatePreset(TOOL_KEY, existing.id, { name: presetName.trim(), config })
        : await toolsAPI.createPreset(TOOL_KEY, { name: presetName.trim(), config });
      setPresetId(res.data.id);
      loadPresets();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the preset');
    } finally {
      setSavingPreset(false);
    }
  }

  async function handleStart() {
    setStarting(true);
    setError('');
    try {
      const payload = {
        show_id: show.id,
        competition_id: resolved.competition_id,
        lane_id: laneId,
        lane_ids: followed,
        tuning,
        preset_id: presetId || null,
        streams: Object.fromEntries(
          STREAMS.filter(s => streams[s.key] && streams[s.key] !== REDACTED).map(s => [s.key, streams[s.key].trim()])
        ),
      };
      const res = await climbBridgeAPI.start(payload);
      onStarted(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not start the bridge');
    } finally {
      setStarting(false);
    }
  }

  const lanes = resolved?.lanes || [];
  const bindableLanes = lanes.filter(l => l.bindable);
  // Before a competition resolves there is nothing to follow; after it, an
  // empty selection would mean "everything", which is never what a tick box
  // that has been cleared means.
  const followed = followIds ?? bindableLanes.map(l => l.laneId);
  const isFollowed = (lane) => followed.includes(lane.laneId);
  const chosenLane = lanes.find(l => l.laneId === laneId) || null;

  function toggleFollow(lane) {
    const next = isFollowed(lane) ? followed.filter(id => id !== lane.laneId) : [...followed, lane.laneId];
    setFollowIds(next);
    // The on-air lane is always followed: it is the one going out.
    if (!next.includes(laneId)) setLaneId(next[0] ?? null);
  }

  function chooseOnAir(lane) {
    setLaneId(lane.laneId);
    if (!isFollowed(lane)) setFollowIds([...followed, lane.laneId]);
  }
  const hasToken = STREAMS.some(s => streams[s.key]);
  const canStart = !!show && !!resolved && !!chosenLane?.bindable && followed.length > 0 && hasToken && !running && !starting;

  return (
    <div className="space-y-6">
      {error && (
        <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
      )}

      {/* Preset + competition */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-white">1. The event</h2>

        {presets.length > 0 && (
          <div>
            <label className={LABEL}>Saved event</label>
            <select value={presetId} onChange={e => applyPreset(e.target.value)} className={FIELD}>
              <option value="">— new —</option>
              {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className={LABEL}>ClimbAlong competition ID</label>
            <input
              placeholder="410, or a ClimbAlong URL containing the ID"
              value={competitionInput}
              onChange={e => setCompetitionInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleResolve()}
              className={FIELD}
            />
          </div>
          <button
            onClick={() => handleResolve()}
            disabled={resolving || !competitionInput.trim()}
            className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#3a3a3a] disabled:opacity-40 text-white text-sm rounded transition-colors"
          >
            {resolving ? 'Resolving…' : 'Resolve'}
          </button>
        </div>
      </section>

      {resolved && (
        <>
          {/* Lane mapping */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-white">2. Lanes</h2>
            <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded">
              <div className="text-sm text-white font-medium">{resolved.competition?.title || `Competition ${resolved.competition_id}`}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {[resolved.competition?.facility, resolved.competition?.city].filter(Boolean).join(', ')}
                {' · '}{resolved.athlete_count} athletes · {resolved.problem_count} problems
              </div>
            </div>

            {resolved.binding_errors?.length > 0 && (
              <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-xs leading-relaxed">
                <strong className="block mb-1">Some lanes cannot be bound.</strong>
                {resolved.binding_errors.map((e, i) => <div key={i}>{e}</div>)}
                <div className="mt-1.5 text-red-300/70">
                  ClimbAlong's own guide says to expect exactly one circuit challenge per lane and to contact them otherwise.
                  These lanes will not be polled.
                </div>
              </div>
            )}

            <div className="border border-[#2a2a2a] rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#1a1a1a] text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-8" title="Record and publish this lane">Rec</th>
                    <th className="text-left px-3 py-2 font-medium w-8" title="Shown by a graphic whose Category is blank">Default</th>
                    <th className="text-left px-3 py-2 font-medium">Lane</th>
                    <th className="text-left px-3 py-2 font-medium">Round</th>
                    <th className="text-left px-3 py-2 font-medium">endNodeId</th>
                    <th className="text-left px-3 py-2 font-medium">circuitId</th>
                    <th className="text-left px-3 py-2 font-medium">Scoring</th>
                  </tr>
                </thead>
                <tbody>
                  {lanes.map(lane => (
                    <tr
                      key={lane.laneId}
                      onClick={() => lane.bindable && chooseOnAir(lane)}
                      className={`border-t border-[#2a2a2a] ${lane.bindable ? 'cursor-pointer hover:bg-[#151515]' : 'opacity-50'} ${laneId === lane.laneId ? 'bg-blue-950/30' : ''} ${lane.bindable && !isFollowed(lane) ? 'opacity-40' : ''}`}
                    >
                      <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={lane.bindable && isFollowed(lane)}
                          onChange={() => toggleFollow(lane)}
                          disabled={!lane.bindable}
                          title="Poll, record and publish this lane"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input type="radio" checked={laneId === lane.laneId} onChange={() => chooseOnAir(lane)} disabled={!lane.bindable} readOnly />
                      </td>
                      <td className="px-3 py-2 text-white">
                        {lane.title}
                        {!lane.bindable && <div className="text-[10px] text-red-400 mt-0.5">{lane.error}</div>}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{lane.roundTitle || '—'}</td>
                      <td className="px-3 py-2 text-gray-500 tabular-nums">{lane.endNodeId}</td>
                      <td className="px-3 py-2 text-gray-500 tabular-nums">{lane.circuitId ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{lane.scoreSystem || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[10px]">
              <span className="text-gray-500">
                Recording <strong className="text-gray-300">{followed.length}</strong> of {bindableLanes.length} lane{bindableLanes.length === 1 ? '' : 's'}
              </span>
              <button type="button" onClick={() => setFollowIds(bindableLanes.map(l => l.laneId))} className="text-blue-500 hover:text-blue-400">All</button>
              <button
                type="button"
                onClick={() => setFollowIds(chosenLane ? [chosenLane.laneId] : [])}
                className="text-blue-500 hover:text-blue-400"
              >
                Only the on-air lane
              </button>
              {chosenLane?.roundTitle && (
                <button
                  type="button"
                  onClick={() => setFollowIds(bindableLanes.filter(l => l.roundTitle === chosenLane.roundTitle).map(l => l.laneId))}
                  className="text-blue-500 hover:text-blue-400"
                >
                  Only {chosenLane.roundTitle}
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-600 leading-relaxed">
              Every ticked lane is polled every cycle, recorded, and published in the standings payload, so each graphic can
              switch between them with its <strong className="text-gray-400">Category</strong> in Singular. Unticked lanes are
              never touched. The default lane is only what a graphic shows when its Category is blank.
            </p>
            <p className="text-[10px] text-gray-600 leading-relaxed">
              <code className="text-gray-500">endNodeId</code> is the leaderboard; <code className="text-gray-500">circuitId</code> is
              the live performances. They are different IDs and ClimbAlong keeps them in different responses — this table is
              where you confirm the bridge resolved both correctly.
            </p>
          </section>

          {/* Streams */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-white">3. Singular data streams</h2>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              A stream's <strong className="text-gray-400">private token</strong>, from the Data Stream's settings in Singular.
              Leave one blank to leave that stream unpublished. Tokens are stored with the preset and never sent back to the browser.
            </p>
            {STREAMS.map(stream => (
              <div key={stream.key}>
                <label className={LABEL}>{stream.label}</label>
                <input
                  type={streams[stream.key] === REDACTED ? 'text' : 'password'}
                  placeholder="private token"
                  value={streams[stream.key]}
                  onChange={e => setStreams(s => ({ ...s, [stream.key]: e.target.value }))}
                  onFocus={e => { if (e.target.value === REDACTED) setStreams(s => ({ ...s, [stream.key]: '' })); }}
                  className={FIELD}
                />
                <p className="text-[10px] text-gray-600 mt-1">{stream.hint}</p>
              </div>
            ))}
          </section>

          {/* Tuning */}
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-white">4. Cadence</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Poll (ms)</label>
                <input
                  type="number" min={500} max={30000} step={250}
                  value={tuning.poll_interval_ms}
                  onChange={e => setTuning(t => ({ ...t, poll_interval_ms: Number(e.target.value) }))}
                  className={FIELD}
                />
                <p className="text-[10px] text-gray-600 mt-1">
                  ClimbAlong recommend 500–1000: their results cache recalculates every 2 s, and polling this often picks up
                  each fresh result as soon as it is ready. This is the largest single term in the delay a viewer sees.
                </p>
              </div>
              <div>
                <label className={LABEL}>Visible rows</label>
                <input
                  type="number" min={1} max={60}
                  value={tuning.visible_rows}
                  onChange={e => setTuning(t => ({ ...t, visible_rows: Number(e.target.value) }))}
                  className={FIELD}
                />
                <p className="text-[10px] text-gray-600 mt-1">How many rows a leaderboard graphic shows. Used as the shedding threshold if a payload nears Singular's 60 KB cap.</p>
              </div>
              <div className="space-y-2 pt-5">
                <label className="flex items-center gap-2 text-xs text-gray-400">
                  <input type="checkbox" checked={tuning.auto_follow} onChange={e => setTuning(t => ({ ...t, auto_follow: e.target.checked }))} />
                  Auto-follow the climber on the wall
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-400">
                  <input type="checkbox" checked={tuning.record_on_start !== false} onChange={e => setTuning(t => ({ ...t, record_on_start: e.target.checked }))} />
                  Record from the start, for playback later
                </label>
              </div>
            </div>
          </section>

          {/* Save + start */}
          <section className="space-y-3 pt-2 border-t border-[#2a2a2a]">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className={LABEL}>Save this binding as</label>
                <input
                  placeholder="e.g. BBCs 2026 — Finals"
                  value={presetName}
                  onChange={e => setPresetName(e.target.value)}
                  className={FIELD}
                />
              </div>
              <button
                onClick={savePreset}
                disabled={savingPreset || !presetName.trim()}
                className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#3a3a3a] disabled:opacity-40 text-white text-sm rounded transition-colors"
              >
                {savingPreset ? 'Saving…' : 'Save preset'}
              </button>
            </div>

            <button
              onClick={handleStart}
              disabled={!canStart}
              className="w-full px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-medium rounded transition-colors"
            >
              {starting ? 'Starting…' : running ? 'Already running — stop it first' : `Start bridge${chosenLane ? ` on ${chosenLane.title}` : ''}`}
            </button>
            {!hasToken && <p className="text-[10px] text-amber-500">Add at least one data stream token — otherwise the bridge would poll and publish nothing.</p>}
            {resolved && followed.length === 0 && <p className="text-[10px] text-amber-500">Tick at least one lane to record.</p>}
            {!show && <p className="text-[10px] text-amber-500">Pick a show first. The bridge binds to one, and its session is recorded against it.</p>}
          </section>
        </>
      )}
    </div>
  );
}
